import EventBus from 'eventing-bus';

interface ExtendedEventBus {
  publish(eventName: string, payload?: unknown): void;
  subscribe(eventName: string, callback: (payload?: unknown) => void): void;
  unregisterCallbacksForEvent(eventName: string): void;
  unregisterCallback(callback: (payload?: unknown) => void): void;
  emit(eventName: string, payload?: unknown): void;
  off(eventName: string): void;
  detach(eventName: string, callback: (payload?: unknown) => void): void;
}

declare global {
  interface Window {
    __pb_eventBus?: ExtendedEventBus;
  }
}

/**
 * Creates a mock EventBus for test environments where the real module might not be available
 */
const createMockEventBus = (): ExtendedEventBus => {
  const callbacks: Record<string, Array<(payload?: unknown) => void>> = {};
  
  const mockBus: ExtendedEventBus = {
    publish: (eventName: string, payload?: unknown): void => {
      if (callbacks[eventName]) {
        callbacks[eventName].forEach(callback => callback(payload));
      }
    },
    subscribe: (eventName: string, callback: (payload?: unknown) => void): void => {
      if (!callbacks[eventName]) {
        callbacks[eventName] = [];
      }
      callbacks[eventName].push(callback);
    },
    unregisterCallbacksForEvent: (eventName: string): void => {
      delete callbacks[eventName];
    },
    unregisterCallback: (callback: (payload?: unknown) => void): void => {
      Object.keys(callbacks).forEach(eventName => {
        callbacks[eventName] = callbacks[eventName].filter(cb => cb !== callback);
      });
    },
    emit: (eventName: string, payload?: unknown): void => {
      mockBus.publish(eventName, payload);
    },
    off: (eventName: string): void => {
      mockBus.unregisterCallbacksForEvent(eventName);
    },
    detach: (eventName: string, callback: (payload?: unknown) => void): void => {
      mockBus.unregisterCallback(callback);
    },
  };
  
  return mockBus;
};

/**
 * Extends an EventBus instance with custom convenience methods
 */
const extendEventBus = (bus: typeof EventBus): ExtendedEventBus => {
  const extended = bus as ExtendedEventBus;
  
  // Add custom convenience methods
  extended.emit = (eventName: string, payload?: unknown): void => {
    extended.publish(eventName, payload);
  };
  
  extended.off = (eventName: string): void => {
    extended.unregisterCallbacksForEvent(eventName);
  };
  
  extended.detach = (eventName: string, callback: (payload?: unknown) => void): void => {
    extended.unregisterCallback(callback);
  };
  
  return extended;
};

// Get or create the base EventBus instance
const getBaseEventBus = (): ExtendedEventBus => {
  // Check if EventBus is available and valid
  if (EventBus && typeof EventBus === 'object' && typeof EventBus.publish === 'function') {
    return extendEventBus(EventBus);
  }

  return createMockEventBus();
};

const baseEventBus = getBaseEventBus();
let eventBusInstance: ExtendedEventBus;

// In SSR mode we have not window object
if (typeof window !== 'undefined') {
  if (!window.__pb_eventBus) {
    window.__pb_eventBus = baseEventBus;
  }
  
  eventBusInstance = window.__pb_eventBus;
} else {
  eventBusInstance = baseEventBus;
}

export default eventBusInstance;

